import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, CaretUp, CaretDown, ArrowsOutLineHorizontal } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Window16PairCorrelation() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activePos, setActivePos] = useState('ball_1+ball_2');
  const [sortConfig, setSortConfig] = useState({ key: 'pred_score', dir: 'desc' });
  const [searchNum1, setSearchNum1] = useState('');
  const [searchNum2, setSearchNum2] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/pair-correlation`); setData(res.data); }
      catch { toast.error('Failed to fetch pair analysis'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const SortIcon = ({ k }) => sortConfig.key !== k ? null : sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;

  const handlePairSearch = async () => {
    const n1 = parseInt(searchNum1), n2 = parseInt(searchNum2);
    if (!n1 || !n2 || n1 === n2 || n1 < 1 || n2 < 1 || n1 > 50 || n2 > 50) {
      toast.error('Enter two different numbers between 1 and 50');
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/analysis/pair-search?num1=${n1}&num2=${n2}`);
      setSearchResult(res.data);
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const posLabels = {
    'ball_1+ball_2': 'B1-B2', 'ball_2+ball_3': 'B2-B3',
    'ball_3+ball_4': 'B3-B4', 'ball_4+ball_5': 'B4-B5',
  };

  const posData = data?.position_data?.[activePos];

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 16</h1><p className="text-xs text-gray-500">Consecutive Pair Analysis (Per Ball Position)</p></div>
            </div>
            <ExportButton data={posData?.pairs} filename="w16_pair_analysis" />
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <WindowNarrative windowId="W16" {...NARRATIVES.W16} className="mb-5" />
        {/* Pair Search Bar */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="pair-search">
          <span className="font-bold text-gray-700 text-sm">Search Pair:</span>
          <input type="number" min="1" max="50" placeholder="Num 1" value={searchNum1} onChange={e => setSearchNum1(e.target.value)}
            className="w-20 px-3 py-2 border rounded font-mono text-center font-bold" data-testid="search-num1"
            onKeyDown={e => e.key === 'Enter' && handlePairSearch()} />
          <span className="text-gray-400 font-bold">&</span>
          <input type="number" min="1" max="50" placeholder="Num 2" value={searchNum2} onChange={e => setSearchNum2(e.target.value)}
            className="w-20 px-3 py-2 border rounded font-mono text-center font-bold" data-testid="search-num2"
            onKeyDown={e => e.key === 'Enter' && handlePairSearch()} />
          <Button onClick={handlePairSearch} disabled={searching} className="bg-[#002FA7]" data-testid="search-btn">
            {searching ? <CircleNotch size={16} className="animate-spin" /> : 'Search'}
          </Button>
          {searchResult && (
            <div className="flex-1 flex flex-wrap items-center gap-4 ml-4 bg-blue-50 rounded-lg px-4 py-2" data-testid="search-result">
              <span className="font-mono font-bold text-xl text-[#002FA7]">{searchResult.pair}</span>
              <span className="text-sm">diff: <strong>{searchResult.diff}</strong></span>
              <span className="text-sm">In same draw: <strong className="text-[#002FA7]">{searchResult.any_position_count}x</strong></span>
              <span className="text-sm">Consecutive: <strong className="text-emerald-700">{searchResult.consecutive_count}x</strong></span>
              <span className="text-sm">Positions: <strong>{Object.entries(searchResult.position_breakdown).map(([k,v]) => `${k}: ${v}x`).join(', ') || 'N/A'}</strong></span>
              <span className="text-sm">Gap: <strong className={searchResult.current_gap > 100 ? 'text-red-600' : ''}>{searchResult.current_gap} draws</strong></span>
              {searchResult.last_appearance && <span className="text-sm">Last: <strong>{searchResult.last_appearance.date}</strong> {searchResult.last_appearance.balls.join(',')}</span>}
              <Button variant="ghost" size="sm" onClick={() => setSearchResult(null)} className="ml-auto text-gray-400">Clear</Button>
            </div>
          )}
        </div>

        {/* Search Result Detail */}
        {searchResult && searchResult.all_appearances.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6" data-testid="search-detail">
            <h3 className="font-bold text-gray-900 mb-2">All Appearances of {searchResult.pair} ({searchResult.all_appearances.length} draws)</h3>
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-center">#</th>
                    <th className="px-3 py-2 text-center">Draw</th>
                    <th className="px-3 py-2 text-center">Date</th>
                    <th className="px-3 py-2 text-center">Balls</th>
                    <th className="px-3 py-2 text-center">Consecutive?</th>
                    <th className="px-3 py-2 text-center">Position</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResult.all_appearances.map((a, idx) => (
                    <tr key={`${a.draw}-${idx}`} className={a.is_consecutive ? 'bg-emerald-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-1 border-b text-gray-400 text-center">{idx + 1}</td>
                      <td className="px-3 py-1 border-b font-mono text-center font-bold">#{a.draw}</td>
                      <td className="px-3 py-1 border-b text-center">{a.date}</td>
                      <td className="px-3 py-1 border-b font-mono text-center">
                        {a.balls.map((b, i) => (
                          <span key={i} className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-xs font-bold mx-0.5 ${b === searchResult.pair.split(',').map(Number)[0] || b === searchResult.pair.split(',').map(Number)[1] ? 'bg-[#002FA7] text-white' : 'bg-gray-100 text-gray-600'}`}>{b}</span>
                        ))}
                      </td>
                      <td className="px-3 py-1 border-b text-center">{a.is_consecutive ? <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-[10px] font-bold">YES</span> : <span className="text-gray-300">No</span>}</td>
                      <td className="px-3 py-1 border-b font-mono text-center font-bold">{a.position || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <Tabs defaultValue="positions" className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
              <TabsTrigger value="positions" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-positions">Per Position</TabsTrigger>
              <TabsTrigger value="recurring" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-recurring">Recurring Pairs</TabsTrigger>
              <TabsTrigger value="recent" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-recent">Last 10 Draws</TabsTrigger>
              <TabsTrigger value="diffs" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-diffs">Diff Distribution</TabsTrigger>
              <TabsTrigger value="coverage" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-coverage">Coverage Analysis</TabsTrigger>
              <TabsTrigger value="hot-pairs" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-hot-pairs">Hot Pairs</TabsTrigger>
              <TabsTrigger value="formation" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-formation">Formation Pattern</TabsTrigger>
              <TabsTrigger value="rare-hot" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white font-bold" data-testid="tab-rare-hot">Rare Hot Pairs</TabsTrigger>
            </TabsList>

            {/* ========= PER POSITION TAB ========= */}
            <TabsContent value="positions">
              {/* Position selector */}
              <div className="flex gap-2 mb-4" data-testid="pos-selector">
                {Object.entries(posLabels).map(([pk, label]) => (
                  <Button key={pk} variant={activePos === pk ? 'default' : 'outline'} size="sm"
                    className={activePos === pk ? 'bg-[#002FA7] text-white' : ''} onClick={() => { setActivePos(pk); setSortConfig({ key: 'pred_score', dir: 'desc' }); }}
                    data-testid={`pos-btn-${label}`}>{label}</Button>
                ))}
              </div>

              {posData && (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-white border rounded p-4">
                      <p className="text-xs uppercase font-bold text-gray-500">Position</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{posData.label}</p>
                    </div>
                    <div className="bg-white border rounded p-4">
                      <p className="text-xs uppercase font-bold text-gray-500">Unique Pairs Seen</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="stat-unique">{posData.total_unique}</p>
                    </div>
                    <div className="bg-white border rounded p-4 bg-amber-50 border-amber-200">
                      <p className="text-xs uppercase font-bold text-amber-600">Top Predicted Pair</p>
                      <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="stat-top-pair">{posData.pairs[0]?.pair} (score {posData.pairs[0]?.pred_score})</p>
                    </div>
                  </div>

                  {/* Main pair table */}
                  <div className="bg-white border rounded p-4">
                    <h3 className="font-bold text-gray-900 mb-3">Top 30 Consecutive Pairs — {posData.label}</h3>
                    <div className="overflow-x-auto" data-testid="pairs-table">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2 py-2 text-left text-[10px] uppercase">#</th>
                            {[['pair','Pair'],['diff','Diff'],['count','Count'],['pct','%'],['r10','R10'],['r20','R20'],['r50','R50'],['expected_gap','Exp Gap'],['current_gap','Gap'],['gap_ratio','Due'],['reversion_bonus','Rev'],['gap_bonus','Gap+'],['pred_score','Score']].map(([k,l]) => (
                              <th key={k} className="px-2 py-2 text-center cursor-pointer hover:bg-gray-200 text-[10px] uppercase" onClick={() => handleSort(k)}>{l} <SortIcon k={k} /></th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[...posData.pairs].sort((a, b) => {
                            let aV = a[sortConfig.key], bV = b[sortConfig.key];
                            if (typeof aV === 'string') return sortConfig.dir === 'desc' ? bV.localeCompare(aV) : aV.localeCompare(bV);
                            return sortConfig.dir === 'desc' ? (bV || 0) - (aV || 0) : (aV || 0) - (bV || 0);
                          }).map((p, idx) => {
                            const isTop5 = [...posData.pairs].sort((a, b) => b.pred_score - a.pred_score).slice(0, 5).some(x => x.pair === p.pair);
                            return (
                              <tr key={p.pair} className={isTop5 ? '' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                style={isTop5 ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.12)'} : {}} data-testid={`pair-${p.pair}`}>
                                <td className="px-2 py-1 border-b text-gray-400">{idx + 1}</td>
                                <td className="px-2 py-1 border-b"><span className="font-mono font-bold text-sm bg-[#002FA7]/10 text-[#002FA7] px-2 py-0.5 rounded">{p.pair}</span></td>
                                <td className={`px-2 py-1 border-b font-mono text-center font-bold ${p.diff <= 9 ? 'text-green-700' : 'text-gray-400'}`}>{p.diff}</td>
                                <td className="px-2 py-1 border-b font-mono text-center font-bold">{p.count}</td>
                                <td className="px-2 py-1 border-b font-mono text-center">{p.pct}%</td>
                                <td className={`px-2 py-1 border-b font-mono text-center ${p.r10 > 0 ? 'font-bold text-green-700' : 'text-gray-300'}`}>{p.r10}</td>
                                <td className="px-2 py-1 border-b font-mono text-center">{p.r20}</td>
                                <td className="px-2 py-1 border-b font-mono text-center">{p.r50}</td>
                                <td className="px-2 py-1 border-b font-mono text-center text-gray-500">{p.expected_gap}</td>
                                <td className={`px-2 py-1 border-b font-mono text-center font-bold ${p.gap_ratio >= 2 ? 'text-red-700 bg-red-50' : p.gap_ratio >= 1.2 ? 'text-orange-600' : 'text-gray-500'}`}>{p.current_gap}</td>
                                <td className={`px-2 py-1 border-b font-mono text-center font-bold ${p.gap_ratio >= 2 ? 'text-red-700' : p.gap_ratio >= 1.2 ? 'text-orange-600' : p.gap_ratio >= 1 ? 'text-yellow-600' : 'text-gray-500'}`}>{p.gap_ratio}x</td>
                                <td className={`px-2 py-1 border-b font-mono text-center font-bold ${p.reversion_bonus > 0 ? 'text-green-600' : p.reversion_bonus < 0 ? 'text-red-500' : 'text-gray-300'}`}>{p.reversion_bonus > 0 ? '+' : ''}{p.reversion_bonus}</td>
                                <td className={`px-2 py-1 border-b font-mono text-center font-bold ${p.gap_bonus > 0 ? 'text-green-600' : p.gap_bonus < 0 ? 'text-red-500' : 'text-gray-300'}`}>{p.gap_bonus > 0 ? '+' : ''}{p.gap_bonus}</td>
                                <td className="px-2 py-1 border-b font-mono text-center font-bold text-lg text-[#002FA7]">{p.pred_score}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Prediction section */}
                  <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-6 text-white" data-testid="prediction-section">
                    <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Predicted Pairs — {posData.label}</h3></div>
                    <p className="text-xs text-white/70 mb-4">Score = Frequency + Multi-Window Reversion (R10/R20/R50) + Gap Due Bonus + Diff 1-9 Bonus (+1.5). Overdue pairs with natural consecutive gaps rank highest.</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {posData.pairs.slice(0, 10).map((p, idx) => (
                        <div key={p.pair} className={`p-3 rounded-lg ${idx === 0 ? 'bg-sky-100 text-sky-900' : 'bg-white/10'}`} data-testid={`pred-pair-${idx}`}>
                          <div className="text-xl font-mono font-bold text-center">{p.pair}</div>
                          <div className="text-[10px] text-center mt-1">
                            <span>diff {p.diff}</span> <span className="mx-1">|</span> <span>{p.count}x total</span>
                          </div>
                          <div className="text-[10px] text-center mt-0.5 opacity-70">
                            Gap: {p.current_gap} ({p.gap_ratio}x due)
                          </div>
                          <div className="text-center font-bold mt-1 text-lg">{p.pred_score}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* ========= RECURRING PAIRS TAB ========= */}
            <TabsContent value="recurring">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[['last_10', 'Last 10 Draws'], ['last_20', 'Last 20 Draws']].map(([key, label]) => (
                  <div key={key} className="bg-white border rounded p-4">
                    <h3 className="font-bold text-gray-900 mb-1">{label} — Recurring Consecutive Pairs</h3>
                    <p className="text-xs text-gray-500 mb-3">Pairs appearing 2+ times at consecutive positions</p>
                    {data.recurring_pairs[key].length === 0 ? (
                      <p className="text-sm text-gray-400 py-4 text-center">No recurring consecutive pairs found</p>
                    ) : (
                      <div className="space-y-2" data-testid={`recurring-${key}`}>
                        {data.recurring_pairs[key].map((r, idx) => (
                          <div key={r.pair} className={`flex items-center justify-between px-3 py-2 rounded ${idx === 0 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                            <div>
                              <span className="font-mono font-bold text-lg text-[#002FA7]">{r.pair}</span>
                              <span className="ml-2 text-xs text-gray-500">at {r.positions.join(', ')}</span>
                            </div>
                            <div className="text-right">
                              <span className="font-bold text-lg text-amber-700">{r.count}x</span>
                              <span className="block text-[10px] text-gray-400">draws: {r.draws.join(', ')}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Never appeared pairs */}
              {data.never_appeared && data.never_appeared.length > 0 && (
                <div className="bg-white border rounded p-4 mt-6">
                  <h3 className="font-bold text-gray-900 mb-1">Never Appeared (Diff 1-9, Common Positions)</h3>
                  <p className="text-xs text-gray-500 mb-3">Pairs with natural diff that haven't appeared yet — high future probability</p>
                  <div className="flex flex-wrap gap-2" data-testid="never-appeared">
                    {data.never_appeared.map((n, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 font-mono text-xs font-bold bg-red-50 text-red-700 border border-red-200 px-2 py-1 rounded">
                        {n.pair} <span className="text-[9px] text-red-400">(d{n.diff}, {n.position})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ========= LAST 10 DRAWS TAB ========= */}
            <TabsContent value="recent">
              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-3">Last 10 Draws — Consecutive Pairs Only</h3>
                <div className="overflow-x-auto" data-testid="recent-table">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-center">Draw</th>
                        <th className="px-3 py-2 text-center">Date</th>
                        <th className="px-3 py-2 text-center">Balls</th>
                        <th className="px-3 py-2 text-center">B1-B2</th>
                        <th className="px-3 py-2 text-center">B2-B3</th>
                        <th className="px-3 py-2 text-center">B3-B4</th>
                        <th className="px-3 py-2 text-center">B4-B5</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.last_10.map((d, idx) => {
                        // Check if any pair recurs across last 10
                        const allPairs = data.last_10.flatMap(x => x.consecutive_pairs.map(p => p.pair));
                        const recurringSet = new Set();
                        const seen = {};
                        allPairs.forEach(p => { seen[p] = (seen[p] || 0) + 1; if (seen[p] >= 2) recurringSet.add(p); });

                        return (
                          <tr key={d.draw} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`draw-${d.draw}`}>
                            <td className="px-3 py-1.5 border-b font-mono text-center font-bold">#{d.draw}</td>
                            <td className="px-3 py-1.5 border-b text-center">{d.date}</td>
                            <td className="px-3 py-1.5 border-b font-mono text-center">{d.balls.join(', ')}</td>
                            {d.consecutive_pairs.map((cp, i) => (
                              <td key={i} className="px-3 py-1.5 border-b text-center">
                                <span className={`inline-block font-mono text-xs font-bold px-2 py-1 rounded ${recurringSet.has(cp.pair) ? 'bg-amber-200 text-amber-900 ring-2 ring-amber-400' : 'bg-gray-100 text-gray-600'}`}>
                                  {cp.pair}
                                </span>
                                <span className={`block text-[9px] mt-0.5 ${cp.diff <= 9 ? 'text-green-600' : 'text-gray-400'}`}>d={cp.diff}</span>
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* ========= DIFF DISTRIBUTION TAB ========= */}
            <TabsContent value="diffs">
              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-1">Consecutive Pair Difference Distribution</h3>
                <p className="text-xs text-gray-500 mb-4">How the gap between consecutive ball positions distributes. Diff 1-9 accounts for the majority.</p>
                <div className="space-y-1" data-testid="diff-dist">
                  {data.diff_stats.map(ds => {
                    const maxPct = Math.max(...data.diff_stats.map(x => x.pct));
                    return (
                      <div key={ds.diff} className="flex items-center gap-2">
                        <span className={`font-mono text-xs font-bold w-8 text-right ${ds.diff <= 9 ? 'text-green-700' : 'text-gray-400'}`}>{ds.diff}</span>
                        <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden relative">
                          <div className={`h-full rounded transition-all ${ds.diff <= 9 ? 'bg-green-500' : 'bg-gray-300'}`} style={{ width: `${(ds.pct / maxPct) * 100}%` }} />
                          <span className="absolute right-2 top-0 text-[10px] font-mono font-bold leading-5">{ds.count}x</span>
                        </div>
                        <span className="font-mono text-xs w-12 text-right font-bold">{ds.pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            {/* ========= COVERAGE ANALYSIS TAB ========= */}
            <TabsContent value="coverage">
              {/* Position selector for coverage */}
              <div className="flex gap-2 mb-4" data-testid="coverage-pos-selector">
                {Object.entries(posLabels).map(([pk, label]) => (
                  <Button key={pk} variant={activePos === pk ? 'default' : 'outline'} size="sm"
                    className={activePos === pk ? 'bg-[#002FA7] text-white' : ''} onClick={() => setActivePos(pk)}
                    data-testid={`cov-btn-${label}`}>{label}</Button>
                ))}
              </div>

              {data.coverage?.[activePos] && (() => {
                const cov = data.coverage[activePos];
                const covPairs = cov.dominant_pairs || [];
                const neverAppeared = covPairs.filter(p => p.count === 0);
                const rarest = [...covPairs].sort((a, b) => a.count - b.count).filter(p => p.count > 0 && !p.in_last_10).slice(0, 20);
                const topDue = covPairs.slice(0, 20);
                return (
                  <div className="space-y-6">
                    {/* Range info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white border rounded p-4">
                        <p className="text-xs uppercase font-bold text-gray-500">{cov.label.split('-')[0]} Range</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{cov.pos1_range.min} - {cov.pos1_range.max}</p>
                        <p className="text-xs text-gray-500">Covers {cov.pos1_range.coverage_pct}% of draws</p>
                      </div>
                      <div className="bg-white border rounded p-4">
                        <p className="text-xs uppercase font-bold text-gray-500">{cov.label.split('-')[1]} Range</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{cov.pos2_range.min} - {cov.pos2_range.max}</p>
                        <p className="text-xs text-gray-500">Covers {cov.pos2_range.coverage_pct}% of draws</p>
                      </div>
                      <div className="bg-white border rounded p-4 bg-red-50 border-red-200">
                        <p className="text-xs uppercase font-bold text-red-600">Coverage Gap</p>
                        <p className="text-2xl font-bold text-red-700 mt-1">{neverAppeared.length} pairs never appeared</p>
                        <p className="text-xs text-red-500">{cov.total_dominant_pairs} total pairs in dominant range</p>
                      </div>
                    </div>

                    {/* Prediction: Top Due Pairs */}
                    <div className="bg-gradient-to-r from-red-700 to-orange-700 rounded-lg p-5 text-white" data-testid="coverage-prediction">
                      <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Highest Reappearance Probability — {cov.label}</h3></div>
                      <p className="text-xs text-white/70 mb-4">Pairs within the dominant range that are most overdue. Score = Gap overdue factor + Rarity factor + Not-in-last-10 bonus + Diff 1-9 bonus + Coverage deficit</p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {topDue.slice(0, 10).map((p, idx) => (
                          <div key={p.pair} className={`p-3 rounded-lg ${idx === 0 ? 'bg-white text-gray-900' : 'bg-white/10'}`} data-testid={`cov-pred-${idx}`}>
                            <div className="text-xl font-mono font-bold text-center">{p.pair}</div>
                            <div className="text-[10px] text-center mt-1 opacity-80">
                              d={p.diff} | {p.count}x/{p.expected} exp
                            </div>
                            <div className="text-[10px] text-center">
                              {p.count === 0 ? <span className="text-red-300 font-bold">NEVER</span> : <span>gap {p.current_gap}</span>}
                              {!p.in_last_10 && p.count > 0 && <span className="ml-1 text-amber-300">not in R10</span>}
                            </div>
                            <div className={`text-center font-bold mt-1 text-lg ${idx === 0 ? 'text-red-700' : ''}`}>{p.due_score}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Full table */}
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-1">Dominant Range Pairs — {cov.label}</h3>
                      <p className="text-xs text-gray-500 mb-3">All pairs within the 80% dominant range, ranked by reappearance probability. Red = never appeared, Amber = not in last 10.</p>
                      <div className="overflow-x-auto" data-testid="coverage-table">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-2 py-2 text-left text-[10px] uppercase">#</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Pair</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Diff</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Count</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Expected</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Deficit</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Gap</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">R10</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Due Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topDue.map((p, idx) => {
                              const isNever = p.count === 0;
                              const isNotRecent = !p.in_last_10 && p.count > 0;
                              return (
                                <tr key={p.pair} className={isNever ? 'bg-red-50' : isNotRecent ? '' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                  style={idx < 5 ? {borderLeft: '4px solid #dc2626', backgroundColor: idx === 0 ? 'rgba(220, 38, 38, 0.12)' : isNever ? undefined : 'rgba(220, 38, 38, 0.05)'} : isNever ? {borderLeft: '3px solid #ef4444'} : {}}
                                  data-testid={`cov-row-${p.pair}`}>
                                  <td className="px-2 py-1 border-b text-gray-400">{idx + 1}</td>
                                  <td className="px-2 py-1 border-b"><span className={`font-mono font-bold text-sm px-2 py-0.5 rounded ${isNever ? 'bg-red-100 text-red-700' : isNotRecent ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>{p.pair}</span></td>
                                  <td className={`px-2 py-1 border-b font-mono text-center font-bold ${p.diff <= 9 ? 'text-green-700' : 'text-gray-400'}`}>{p.diff}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center font-bold ${isNever ? 'text-red-600' : p.count <= 3 ? 'text-orange-600' : ''}`}>{p.count === 0 ? 'NEVER' : p.count}</td>
                                  <td className="px-2 py-1 border-b font-mono text-center text-gray-500">{p.expected}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center font-bold ${p.deficit > 0 ? 'text-red-600' : p.deficit < -2 ? 'text-green-600' : 'text-gray-400'}`}>{p.deficit > 0 ? '+' : ''}{p.deficit}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center ${p.current_gap > 500 ? 'font-bold text-red-600' : ''}`}>{p.current_gap}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center ${p.r10 > 0 ? 'font-bold text-green-700' : 'text-gray-300'}`}>{p.r10}</td>
                                  <td className="px-2 py-1 border-b font-mono text-center font-bold text-lg text-red-700">{p.due_score}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </TabsContent>

            {/* ========= HOT PAIRS TAB ========= */}
            <TabsContent value="hot-pairs">
              {/* Hot Numbers Overview */}
              <div className="bg-white border rounded p-4 mb-6">
                <h3 className="font-bold text-gray-900 mb-1">Hot Numbers (Recent Draws)</h3>
                <p className="text-xs text-gray-500 mb-3">Numbers appearing 3+ times in last 10 or 5+ in last 20 are marked HOT. When both numbers in a pair are hot, that pair has elevated chances.</p>
                <div className="flex flex-wrap gap-2" data-testid="hot-numbers-grid">
                  {data.hot_numbers?.slice(0, 20).map(h => (
                    <div key={h.number} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg font-mono ${h.is_hot ? 'bg-red-100 border-2 border-red-400 text-red-900 font-bold' : 'bg-gray-100 border border-gray-200 text-gray-600'}`} data-testid={`hot-num-${h.number}`}>
                      <span className="text-lg font-bold">{h.number}</span>
                      <span className="text-[10px]">R10:{h.r10} R20:{h.r20}</span>
                      {h.is_hot && <span className="text-[9px] bg-red-600 text-white px-1 rounded">HOT</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Position selector for hot pairs */}
              <div className="flex gap-2 mb-4">
                {Object.entries(posLabels).map(([pk, label]) => (
                  <Button key={pk} variant={activePos === pk ? 'default' : 'outline'} size="sm"
                    className={activePos === pk ? 'bg-[#002FA7] text-white' : ''} onClick={() => setActivePos(pk)}
                    data-testid={`hot-btn-${label}`}>{label}</Button>
                ))}
              </div>

              {data.hot_pair_candidates?.[activePos] && (() => {
                const hpc = data.hot_pair_candidates[activePos];
                return (
                  <>
                    {/* Prediction: Top Hot Pairs */}
                    <div className="bg-gradient-to-r from-red-600 to-pink-600 rounded-lg p-5 text-white mb-6" data-testid="hot-pairs-prediction">
                      <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Hot Pair Candidates — {hpc.label}</h3></div>
                      <p className="text-xs text-white/70 mb-4">Both numbers are hot in recent draws + diff 1-11. Score = Individual hotness + Rarity (lower count = higher) + Gap factor + Not-in-R10 bonus. If a pair has NEVER appeared with both numbers hot, chances are highest.</p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {hpc.candidates.slice(0, 10).map((c, idx) => (
                          <div key={c.pair} className={`p-3 rounded-lg ${idx === 0 ? 'bg-white text-gray-900' : 'bg-white/10'}`} data-testid={`hot-cand-${idx}`}>
                            <div className="text-xl font-mono font-bold text-center">{c.pair}</div>
                            <div className="flex justify-center gap-1 mt-1">
                              <span className={`text-[10px] px-1 rounded ${c.v1_hot >= 3 ? 'bg-red-200 text-red-800' : 'bg-gray-200 text-gray-600'}`}>{c.v1}:R{c.v1_hot}</span>
                              <span className={`text-[10px] px-1 rounded ${c.v2_hot >= 3 ? 'bg-red-200 text-red-800' : 'bg-gray-200 text-gray-600'}`}>{c.v2}:R{c.v2_hot}</span>
                            </div>
                            <div className="text-[10px] text-center mt-1 opacity-80">
                              d={c.diff} | {c.count === 0 ? <span className="font-bold text-red-300">NEVER</span> : `${c.count}x`} | gap {c.gap}
                            </div>
                            <div className={`text-center font-bold mt-1 text-lg ${idx === 0 ? 'text-red-700' : ''}`}>{c.hot_score}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Hot Pairs Table */}
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-3">All Hot Pair Candidates — {hpc.label}</h3>
                      <div className="overflow-x-auto" data-testid="hot-pairs-table">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-2 py-2 text-left text-[10px] uppercase">#</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Pair</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Diff</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">V1 Hot</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">V2 Hot</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Count</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Gap</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Pair R10</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Hot Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {hpc.candidates.map((c, idx) => (
                              <tr key={c.pair} className={idx < 3 ? '' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                style={idx < 3 ? {borderLeft: '4px solid #dc2626', backgroundColor: idx === 0 ? 'rgba(220, 38, 38, 0.12)' : 'rgba(220, 38, 38, 0.05)'} : {}}>
                                <td className="px-2 py-1 border-b text-gray-400">{idx + 1}</td>
                                <td className="px-2 py-1 border-b"><span className="font-mono font-bold text-sm bg-red-100 text-red-700 px-2 py-0.5 rounded">{c.pair}</span></td>
                                <td className={`px-2 py-1 border-b font-mono text-center font-bold ${c.diff <= 9 ? 'text-green-700' : 'text-gray-500'}`}>{c.diff}</td>
                                <td className="px-2 py-1 border-b font-mono text-center"><span className={`px-1.5 rounded ${c.v1_hot >= 3 ? 'bg-red-100 text-red-700 font-bold' : 'text-gray-500'}`}>{c.v1_hot}</span></td>
                                <td className="px-2 py-1 border-b font-mono text-center"><span className={`px-1.5 rounded ${c.v2_hot >= 3 ? 'bg-red-100 text-red-700 font-bold' : 'text-gray-500'}`}>{c.v2_hot}</span></td>
                                <td className={`px-2 py-1 border-b font-mono text-center font-bold ${c.count === 0 ? 'text-red-600' : c.count <= 3 ? 'text-orange-600' : ''}`}>{c.count === 0 ? 'NEVER' : c.count}</td>
                                <td className={`px-2 py-1 border-b font-mono text-center ${c.gap > 500 ? 'font-bold text-red-600' : ''}`}>{c.gap}</td>
                                <td className={`px-2 py-1 border-b font-mono text-center ${c.r10_pair > 0 ? 'font-bold text-green-700' : 'text-gray-300'}`}>{c.r10_pair}</td>
                                <td className="px-2 py-1 border-b font-mono text-center font-bold text-lg text-red-700">{c.hot_score}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </TabsContent>

            {/* ========= FORMATION PATTERN TAB ========= */}
            <TabsContent value="formation">
              {data.formation_pattern && (() => {
                const fp = data.formation_pattern;
                return (
                  <div className="space-y-6">
                    {/* Formation Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white border rounded p-4">
                        <p className="text-xs uppercase font-bold text-gray-500">Formation Rate</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{fp.formation_rate}%</p>
                        <p className="text-xs text-gray-500">{fp.historical_count} formations in {data.total_draws} draws</p>
                      </div>
                      <div className="bg-white border rounded p-4 bg-blue-50 border-blue-200">
                        <p className="text-xs uppercase font-bold text-blue-600">Last Draw</p>
                        <div className="flex gap-1 mt-1">
                          {fp.last_draw_nums.map(n => (
                            <span key={n} className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-bold text-sm ${data.hot_numbers?.find(h => h.number === n)?.is_hot ? 'bg-red-500 text-white' : 'bg-[#002FA7] text-white'}`}>{n}</span>
                          ))}
                        </div>
                      </div>
                      <div className="bg-white border rounded p-4 bg-purple-50 border-purple-200">
                        <p className="text-xs uppercase font-bold text-purple-600">Previous Draw</p>
                        <div className="flex gap-1 mt-1">
                          {fp.prev_draw_nums.map(n => (
                            <span key={n} className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-bold text-sm ${data.hot_numbers?.find(h => h.number === n)?.is_hot ? 'bg-red-500 text-white' : 'bg-purple-600 text-white'}`}>{n}</span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Predicted formations for next draw */}
                    <div className="bg-gradient-to-r from-purple-700 to-indigo-700 rounded-lg p-5 text-white" data-testid="formation-prediction">
                      <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Predicted Pair Formations for Next Draw</h3></div>
                      <p className="text-xs text-white/70 mb-4">Numbers from last 2 draws (diff 1-11) that could form consecutive pairs. Score = Recency (in last/prev draw) + Both Hot bonus (+4) + Small diff bonus. Pattern: numbers appear separately in consecutive draws, then form a pair.</p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {fp.predicted_next.slice(0, 10).map((p, idx) => (
                          <div key={p.pair} className={`p-3 rounded-lg ${idx === 0 ? 'bg-white text-gray-900' : 'bg-white/10'}`} data-testid={`form-pred-${idx}`}>
                            <div className="text-xl font-mono font-bold text-center">{p.pair}</div>
                            {p.both_hot && <div className="text-[10px] text-center font-bold text-red-300 mt-0.5">{idx === 0 ? <span className="text-red-600">BOTH HOT</span> : 'BOTH HOT'}</div>}
                            <div className="text-[10px] text-center mt-1 opacity-80">
                              d={p.diff} | {p.a_in_last ? 'Last' : p.a_in_prev ? 'Prev' : ''} + {p.b_in_last ? 'Last' : p.b_in_prev ? 'Prev' : ''}
                            </div>
                            <div className={`text-center font-bold mt-1 text-lg ${idx === 0 ? 'text-purple-700' : ''}`}>{p.form_score}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent historical formations */}
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-1">Recent Formation Examples</h3>
                      <p className="text-xs text-gray-500 mb-3">Pairs that formed after both numbers appeared separately in the previous 1-2 draws. This pattern occurs in {fp.formation_rate}% of all draws.</p>
                      <div className="overflow-x-auto" data-testid="formation-history">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-center">Draw</th>
                              <th className="px-3 py-2 text-center">Date</th>
                              <th className="px-3 py-2 text-center">Pair Formed</th>
                              <th className="px-3 py-2 text-center">Number A appeared in</th>
                              <th className="px-3 py-2 text-center">Number B appeared in</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fp.recent_examples.map((f, idx) => (
                              <tr key={`${f.pair}-${f.draw}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-3 py-1.5 border-b font-mono text-center font-bold">#{f.draw}</td>
                                <td className="px-3 py-1.5 border-b text-center">{f.date}</td>
                                <td className="px-3 py-1.5 border-b text-center"><span className="font-mono font-bold text-lg bg-purple-100 text-purple-800 px-3 py-1 rounded">{f.pair}</span></td>
                                <td className="px-3 py-1.5 border-b text-center font-mono">{f.a_in.trim()}</td>
                                <td className="px-3 py-1.5 border-b text-center font-mono">{f.b_in.trim()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </TabsContent>

            {/* ========= RARE HOT PAIRS TAB ========= */}
            <TabsContent value="rare-hot">
              {/* Position selector */}
              <div className="flex gap-2 mb-4">
                {Object.entries(posLabels).map(([pk, label]) => (
                  <Button key={pk} variant={activePos === pk ? 'default' : 'outline'} size="sm"
                    className={activePos === pk ? 'bg-amber-600 text-white' : ''} onClick={() => setActivePos(pk)}
                    data-testid={`rhp-btn-${label}`}>{label}</Button>
                ))}
              </div>

              {data.rare_hot_pairs?.[activePos] && (() => {
                const rhp = data.rare_hot_pairs[activePos];
                const extremePairs = rhp.pairs.filter(p => p.extreme);
                const bothHotPairs = rhp.pairs.filter(p => p.both_hot);
                return (
                  <>
                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-white border rounded p-4">
                        <p className="text-xs uppercase font-bold text-gray-500">Total Pairs (d 1-11)</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="rhp-total">{rhp.total_pairs}</p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded p-4">
                        <p className="text-xs uppercase font-bold text-red-600">Extreme Signal</p>
                        <p className="text-2xl font-bold text-red-700 mt-1" data-testid="rhp-extreme">{rhp.extreme_count}</p>
                        <p className="text-[10px] text-red-500">Both hot + in last 15 draws</p>
                      </div>
                      <div className="bg-orange-50 border border-orange-200 rounded p-4">
                        <p className="text-xs uppercase font-bold text-orange-600">Both Numbers Hot</p>
                        <p className="text-2xl font-bold text-orange-700 mt-1" data-testid="rhp-both-hot">{rhp.both_hot_count}</p>
                        <p className="text-[10px] text-orange-500">R10 &ge; 3 or R20 &ge; 5</p>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded p-4">
                        <p className="text-xs uppercase font-bold text-blue-600">Both in Last 15</p>
                        <p className="text-2xl font-bold text-blue-700 mt-1" data-testid="rhp-both-consec">{rhp.both_consec_count}</p>
                        <p className="text-[10px] text-blue-500">Both appeared in last 15 draws</p>
                      </div>
                    </div>

                    {/* Extreme Prediction Cards */}
                    {extremePairs.length > 0 && (
                      <div className="bg-gradient-to-r from-red-700 via-amber-700 to-orange-700 rounded-lg p-5 text-white mb-6" data-testid="rhp-extreme-section">
                        <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Extreme High Probability — {rhp.label}</h3></div>
                        <p className="text-xs text-white/70 mb-4">Rare pairs (low count, long gap) where BOTH numbers are hot AND appeared in last 15 draws. Highest convergence of signals.</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {extremePairs.slice(0, 8).map((p, idx) => (
                            <div key={p.pair} className={`p-3 rounded-lg ${idx === 0 ? 'bg-white text-gray-900' : 'bg-white/10'}`} data-testid={`rhp-extreme-${idx}`}>
                              <div className="text-xl font-mono font-bold text-center">{p.pair}</div>
                              <div className="text-[9px] text-center mt-1 font-bold text-red-300">{idx === 0 ? <span className="text-red-600">EXTREME</span> : 'EXTREME'}</div>
                              <div className="flex justify-center gap-1 mt-1">
                                <span className="text-[10px] bg-red-200 text-red-800 px-1 rounded">{p.v1}:R15={p.v1_r15}</span>
                                <span className="text-[10px] bg-red-200 text-red-800 px-1 rounded">{p.v2}:R15={p.v2_r15}</span>
                              </div>
                              <div className="text-[10px] text-center mt-1 opacity-80">
                                d={p.diff} | {p.count === 0 ? <span className="font-bold text-red-300">NEVER</span> : `${p.count}x`} | gap {p.current_gap}
                              </div>
                              <div className={`text-center font-bold mt-1 text-lg ${idx === 0 ? 'text-amber-700' : ''}`}>{p.signal_score}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Both Hot Prediction Cards (non-extreme) */}
                    {bothHotPairs.length > 0 && (
                      <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-lg p-5 text-white mb-6" data-testid="rhp-hot-section">
                        <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Both Hot + Rare — {rhp.label}</h3></div>
                        <p className="text-xs text-white/70 mb-4">Both numbers are individually hot (R10&ge;3 or R20&ge;5) with diff 1-11. Lower historical count = higher rarity score.</p>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          {bothHotPairs.slice(0, 10).map((p, idx) => (
                            <div key={p.pair} className={`p-3 rounded-lg ${idx === 0 ? 'bg-white text-gray-900' : 'bg-white/10'}`} data-testid={`rhp-hot-${idx}`}>
                              <div className="text-xl font-mono font-bold text-center">{p.pair}</div>
                              {p.extreme && <div className="text-[9px] text-center font-bold text-red-300">EXTREME</div>}
                              <div className="flex justify-center gap-1 mt-1">
                                <span className={`text-[10px] px-1 rounded ${p.v1_hot ? 'bg-red-200 text-red-800' : 'bg-gray-200 text-gray-600'}`}>{p.v1}:R{p.v1_r10}</span>
                                <span className={`text-[10px] px-1 rounded ${p.v2_hot ? 'bg-red-200 text-red-800' : 'bg-gray-200 text-gray-600'}`}>{p.v2}:R{p.v2_r10}</span>
                              </div>
                              <div className="text-[10px] text-center mt-1 opacity-80">
                                d={p.diff} | {p.count === 0 ? <span className="font-bold text-red-300">NEVER</span> : `${p.count}x`} | gap {p.current_gap}
                              </div>
                              <div className={`text-center font-bold mt-1 text-lg ${idx === 0 ? 'text-amber-700' : ''}`}>{p.signal_score}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Full Table */}
                    <div className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-1">All Rare Hot Pair Candidates — {rhp.label}</h3>
                      <p className="text-xs text-gray-500 mb-3">Diff 1-11 pairs ranked by signal score. Rarity (low count) + Gap overdue + Hot bonus (both numbers hot) + Last 15 draws bonus.</p>
                      <div className="overflow-x-auto" data-testid="rhp-table">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-2 py-2 text-left text-[10px] uppercase">#</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Pair</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Diff</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Count</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Gap</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">R15</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">V1 R15</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">V2 R15</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Hot</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">In L15</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Rarity</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Gap+</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Hot+</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">L15+</th>
                              <th className="px-2 py-2 text-center text-[10px] uppercase">Signal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rhp.pairs.map((p, idx) => {
                              const isExtreme = p.extreme;
                              const isBothHot = p.both_hot;
                              return (
                                <tr key={p.pair}
                                  className={isExtreme ? '' : isBothHot ? '' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                  style={isExtreme ? {borderLeft: '4px solid #dc2626', backgroundColor: 'rgba(220, 38, 38, 0.10)'} : isBothHot ? {borderLeft: '4px solid #f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.08)'} : {}}
                                  data-testid={`rhp-row-${p.pair}`}>
                                  <td className="px-2 py-1 border-b text-gray-400">{idx + 1}</td>
                                  <td className="px-2 py-1 border-b">
                                    <span className={`font-mono font-bold text-sm px-2 py-0.5 rounded ${isExtreme ? 'bg-red-100 text-red-700' : isBothHot ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>{p.pair}</span>
                                    {isExtreme && <span className="ml-1 text-[8px] bg-red-600 text-white px-1 rounded font-bold">EXTREME</span>}
                                  </td>
                                  <td className={`px-2 py-1 border-b font-mono text-center font-bold ${p.diff <= 9 ? 'text-green-700' : 'text-gray-500'}`}>{p.diff}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center font-bold ${p.count === 0 ? 'text-red-600' : p.count <= 3 ? 'text-orange-600' : ''}`}>{p.count === 0 ? 'NEVER' : p.count}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center ${p.current_gap > 200 ? 'font-bold text-red-600' : ''}`}>{p.current_gap}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center ${p.r15 > 0 ? 'font-bold text-green-700' : 'text-gray-300'}`}>{p.r15}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center ${p.v1_r15 >= 3 ? 'font-bold text-red-600' : p.v1_r15 > 0 ? 'text-orange-600' : 'text-gray-300'}`}>{p.v1_r15}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center ${p.v2_r15 >= 3 ? 'font-bold text-red-600' : p.v2_r15 > 0 ? 'text-orange-600' : 'text-gray-300'}`}>{p.v2_r15}</td>
                                  <td className="px-2 py-1 border-b text-center">
                                    {p.both_hot ? <span className="text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">BOTH</span>
                                    : (p.v1_hot || p.v2_hot) ? <span className="text-[9px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">1 HOT</span>
                                    : <span className="text-gray-300">-</span>}
                                  </td>
                                  <td className="px-2 py-1 border-b text-center">
                                    {p.both_in_consec ? <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">BOTH</span>
                                    : <span className="text-gray-300">-</span>}
                                  </td>
                                  <td className={`px-2 py-1 border-b font-mono text-center ${p.rarity_score >= 20 ? 'font-bold text-red-600' : p.rarity_score >= 10 ? 'text-orange-600' : 'text-gray-500'}`}>{p.rarity_score}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center ${p.gap_bonus >= 10 ? 'font-bold text-red-600' : p.gap_bonus > 0 ? 'text-orange-600' : 'text-gray-300'}`}>{p.gap_bonus}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center ${p.hot_bonus >= 15 ? 'font-bold text-red-600' : p.hot_bonus > 0 ? 'text-orange-600' : 'text-gray-300'}`}>{p.hot_bonus}</td>
                                  <td className={`px-2 py-1 border-b font-mono text-center ${p.consec_bonus >= 12 ? 'font-bold text-blue-600' : p.consec_bonus > 0 ? 'text-blue-400' : 'text-gray-300'}`}>{p.consec_bonus}</td>
                                  <td className="px-2 py-1 border-b font-mono text-center font-bold text-lg text-amber-700">{p.signal_score}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </TabsContent>
          </Tabs>
        ) : null}
      </main>
    </div>
  );
}
